import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, of, forkJoin } from 'rxjs';
import { takeUntil, catchError, finalize, timeout } from 'rxjs/operators';
import { AuthService } from '../auth-o3/shared/services/auth.service';
import { ApplicationsService } from '../auth-o3/admin/services/applications.service';
import { RestrictIfService } from '../auth-o3/shared/restrict/restrict-if.service';
import { RoleUtils } from '../utils/role.utils';
import { ApplicationTokenService } from '../auth-o3/shared/services/application-token.service';
import { ModalService } from '../ui-kit/dc-modal/modal.service';

interface Tile {
  title: string;
  description: string;
  link: string;
  isInternal?: boolean;
  applicationId?: string;
  targetUrl?: string;
}

@Component({
  selector: 'app-landing',
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss'],
})
export class LandingComponent implements OnInit, OnDestroy {
  user: string = '';
  email: string = '';
  isAuthenticated: boolean = false;
  currentUserScopes: string[] = [];
  private destroy$ = new Subject<void>();

  tiles: Tile[] = [];
  loading: boolean = true;
  hasAdminAccess: boolean = false;
  loadingError: boolean = false;
  applicationsLoadFailed: boolean = false;
  applicationsLoaded: boolean = false;
  adminPermissionsLoaded: boolean = false;
  private tilesLoadedOnce: boolean = false;
  private permissionsChecked: boolean = false;
  private hasAttemptedRetry: boolean = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private applicationsService: ApplicationsService,
    private restrictIfService: RestrictIfService,
    private applicationTokenService: ApplicationTokenService,
    private modalService: ModalService
  ) {}

  ngOnInit() {
    const currentSession = this.authService.userSessionSubject.value;
    if (currentSession?.isValid()) {
      this.isAuthenticated = true;
      this.user = currentSession.idToken.payload['cognito:username'];
      this.email = currentSession.idToken.payload.email;
      this.currentUserScopes = currentSession.accessToken.payload.scope
        ? currentSession.accessToken.payload.scope.split(' ')
        : [];
      this.tilesLoadedOnce = true;
      this.loadDynamicTiles();
    }

    this.authService.onUserSession.pipe(takeUntil(this.destroy$)).subscribe({
      next: (session) => {
        if (session?.isValid() && !this.tilesLoadedOnce) {
          this.isAuthenticated = true;
          this.user = session.idToken.payload['cognito:username'];
          this.email = session.idToken.payload.email;
          this.currentUserScopes = session.accessToken.payload.scope
            ? session.accessToken.payload.scope.split(' ')
            : [];

          this.tilesLoadedOnce = true;
          this.loadDynamicTiles();
        } else if (!session?.isValid()) {
          this.isAuthenticated = false;
          this.user = '';
          this.email = '';
          this.currentUserScopes = [];
          this.tiles = [];
          this.loading = false;
          this.tilesLoadedOnce = false;
          this.hasAttemptedRetry = false;
        }
      },
      error: (error) => {
        console.error('Error getting session:', error);
        this.loading = false;
      },
    });
  }

  private loadDynamicTiles() {
    this.loading = true;
    this.loadingError = false;
    this.applicationsLoadFailed = false;
    this.applicationsLoaded = false;
    this.adminPermissionsLoaded = false;

    const userRoles = this.authService.getUserRolesFromToken();

    this.applicationsService
      .getApplications()
      .pipe(
        timeout(5000),
        catchError((error) => {
          console.error('Error loading applications:', error);
          this.applicationsLoadFailed = true;
          return of([]);
        }),
        finalize(() => {
          this.applicationsLoaded = true;
          this.checkIfLoadingComplete();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (applications) => {
          const userApps = RoleUtils.getAccessibleApplications(userRoles);

          const matchingApps = applications.filter((app) =>
            userApps.has(app.applicationId)
          );

          const dynamicTiles: Tile[] = matchingApps.map((app) => ({
            title: RoleUtils.formatApplicationNameForDisplay(app.applicationId),
            description: app.parentApplicationId
              ? RoleUtils.formatApplicationNameForDisplay(app.parentApplicationId)
              : app.applicationId,
            link: app.uri,
            isInternal: false,
            applicationId: app.applicationId,
            targetUrl: app.uri,
          }));

          this.tiles = this.buildTilesList(dynamicTiles);
        },
        error: (error) => {
          console.error('Error loading tiles:', error);
          this.loadingError = true;
          this.hasAdminAccess = false;
          this.tiles = this.buildTilesList([]);
        },
      });

    this.restrictIfService
      .restrictTo('VIEW_ADMIN_SECTION')
      .pipe(
        timeout(15000),
        catchError((error) => {
          console.error('Error checking admin permission:', error);
          return of(false);
        }),
        finalize(() => {
          this.adminPermissionsLoaded = true;
          this.checkIfLoadingComplete();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (hasPermission) => {
          if (this.hasAdminAccess !== hasPermission) {
            this.hasAdminAccess = hasPermission;
            const currentDynamicTiles = this.tiles.filter(
              (t) => t.title !== 'Admin Dashboard'
            );
            this.tiles = this.buildTilesList(currentDynamicTiles);
          }
        },
      });
  }

  private checkIfLoadingComplete(): void {
    if (this.applicationsLoaded && this.adminPermissionsLoaded) {
      this.loading = false;

      const isNoAppsError =
        !this.loadingError &&
        !this.applicationsLoadFailed &&
        this.tiles.length === 0;

      if (isNoAppsError && !this.hasAttemptedRetry) {
        this.hasAttemptedRetry = true;
        this.loadDynamicTiles();
      }
    }
  }

  private buildTilesList(dynamicTiles: Tile[]): Tile[] {
    const tiles: Tile[] = [];

    if (this.hasAdminAccess) {
      tiles.push({
        title: 'Admin Dashboard',
        description: 'User management and system configuration',
        link: '/app/admin/users',
        isInternal: true,
      });
    }

    tiles.push(...dynamicTiles);

    return tiles;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async signOut() {
    this.isAuthenticated = false;
    this.user = '';
    this.email = '';
    this.currentUserScopes = [];

    await this.authService.signOut();
    this.router.navigate(['/login']);
  }

  onTileClick(tile: Tile) {
    if (tile.isInternal) {
      this.router.navigateByUrl(tile.link);
    } else if (tile.applicationId && tile.targetUrl) {
      this.applicationTokenService
        .authenticateAndRedirect(tile.applicationId, tile.targetUrl)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          error: (error) => {
            console.error('SSO authentication failed:', error);
            this.modalService.openDialog({
              title: 'Authentication Failure',
              body: '<p>Authentication failure: please try accessing again. If issue persists, please contact the support team.</p>',
              buttons: {
                positive: 'OK',
              },
              size: 'md',
              backdrop: 'static',
            });
          },
        });
    } else {
      console.error('Invalid tile configuration', {
        title: tile.title,
        missingApplicationId: !tile.applicationId,
        missingTargetUrl: !tile.targetUrl,
      });
      alert(
        'This application is not configured correctly. Please contact support.'
      );
    }
  }
}
